/**
 * Accounts Receivable Aging report.
 *
 * - Per-customer totals aged into Current / 1-30 / 31-60 / 61-90 / 90+ day buckets
 * - Per-invoice detail table below
 * - Grand total row + export (PDF / Excel)
 */
import { FileText, FileSpreadsheet, Bell, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";

const BUCKETS = ["current", "d1_30", "d31_60", "d61_90", "d90_plus"];
const BUCKET_LABELS = {
  current:  "Current",
  d1_30:    "1 - 30",
  d31_60:   "31 - 60",
  d61_90:   "61 - 90",
  d90_plus: "90+",
};
const BUCKET_COLORS = {
  current:  "#166534",
  d1_30:    "#854D0E",
  d31_60:   "#B45309",
  d61_90:   "#9A3412",
  d90_plus: "#991B1B",
};

export default function ARAgingView({ aging, onExport, onShare, asOf, onReload }) {
  const totals = aging?.totals || {};
  const customers = aging?.customers || [];
  const invoices = aging?.invoices || [];
  const grandTotal = aging?.grand_total || 0;
  const [remindingId, setRemindingId] = useState(null);

  const sendReminder = async (invoiceId) => {
    setRemindingId(invoiceId);
    try {
      const r = await api.post(`/invoices/${invoiceId}/remind`);
      if (r.data.delivery_mode === "mailto" && r.data.mailto_url) {
        window.location.href = r.data.mailto_url;
        toast.info("Reminder draft opened in your mail client");
      } else if (r.data.delivered) toast.success(`Reminder sent · attempt #${r.data.reminder_count}`);
      else toast.info(`Reminder logged (delivery failed). Attempt #${r.data.reminder_count}`);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reminder failed");
    } finally {
      setRemindingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ar-aging-view">
      {/* Header + exports */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ll-ink-muted)]">Receivables</div>
          <h2 className="font-display font-bold text-2xl">Accounts Receivable Aging</h2>
          <div className="text-sm text-[var(--ll-ink-soft)] mt-0.5">
            As of {asOf ? fmtDate(asOf) : fmtDate(aging.as_of)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onExport("ar-aging", "pdf")}
            data-testid="ar-export-pdf"
            className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileText size={14} /> PDF
          </button>
          <button
            onClick={() => onExport("ar-aging", "xlsx")}
            data-testid="ar-export-xlsx"
            className="text-sm px-3 py-2 border hover:bg-[var(--ll-mint)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
          {onShare && (
            <button
              onClick={() => onShare("ar-aging")}
              data-testid="ar-share-email"
              title="Share by email"
              className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 flex items-center gap-1.5"
            >
              <Mail size={14} /> Share by email
            </button>
          )}
        </div>
      </div>

      {/* Bucket KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="ar-bucket-strip">
        {BUCKETS.map((k) => (
          <div
            key={k}
            data-testid={`ar-bucket-${k}`}
            className="p-3 border"
            style={{ borderColor: "var(--ll-border)", borderTop: `3px solid ${BUCKET_COLORS[k]}` }}
          >
            <div className="text-[10px] uppercase tracking-widest" style={{ color: BUCKET_COLORS[k] }}>
              {BUCKET_LABELS[k]}
            </div>
            <div className="font-mono-fin text-lg mt-1">{fmtMoney(totals[k] || 0)}</div>
          </div>
        ))}
        <div
          className="p-3 border bg-[#0b5d9e] text-white"
          data-testid="ar-grand-total"
          style={{ borderColor: "#0b5d9e" }}
        >
          <div className="text-[10px] uppercase tracking-widest opacity-80">Total A/R</div>
          <div className="font-mono-fin text-lg mt-1">{fmtMoney(grandTotal)}</div>
        </div>
      </div>

      {/* Per-customer rollup */}
      <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }}>
        <div className="px-4 py-2.5 bg-[#0b5d9e] text-white text-[10px] uppercase tracking-[0.2em] font-medium">
          By customer
        </div>
        {customers.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--ll-ink-soft)]" data-testid="ar-empty">
            No outstanding receivables.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--ll-bg-card)] text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">
                <th className="px-4 py-2">Customer</th>
                {BUCKETS.map((k) => (
                  <th key={k} className="px-3 py-2 text-right">{BUCKET_LABELS[k]}</th>
                ))}
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c, i) => (
                <tr key={i} className="border-t" style={{ borderColor: "var(--ll-border)" }} data-testid={`ar-customer-row-${i}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.bill_to_name}</div>
                    <div className="text-xs text-[var(--ll-ink-soft)]">{c.invoice_count} invoice{c.invoice_count === 1 ? "" : "s"}</div>
                  </td>
                  {BUCKETS.map((k) => (
                    <td key={k} className="px-3 py-2 text-right font-mono-fin" style={{ color: c[k] > 0 ? BUCKET_COLORS[k] : "var(--ll-ink-muted)" }}>
                      {c[k] > 0 ? fmtMoney(c[k]) : "—"}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right font-mono-fin font-bold">{fmtMoney(c.total)}</td>
                </tr>
              ))}
              <tr className="border-t bg-[#0b5d9e] text-white" style={{ borderColor: "#0b5d9e" }} data-testid="ar-totals-row">
                <td className="px-4 py-2.5 font-bold text-[10px] uppercase tracking-widest">Total</td>
                {BUCKETS.map((k) => (
                  <td key={k} className="px-3 py-2.5 text-right font-mono-fin font-bold">{fmtMoney(totals[k] || 0)}</td>
                ))}
                <td className="px-4 py-2.5 text-right font-mono-fin font-bold">{fmtMoney(grandTotal)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Open invoices detail */}
      {invoices.length > 0 && (
        <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }}>
          <div className="px-4 py-2.5 bg-[#0b5d9e] text-white text-[10px] uppercase tracking-[0.2em] font-medium">
            Open invoices
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--ll-bg-card)] text-left text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)]">
                <th className="px-4 py-2">Invoice #</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Invoice Date</th>
                <th className="px-4 py-2">Due Date</th>
                <th className="px-4 py-2 text-right">Days Overdue</th>
                <th className="px-4 py-2">Bucket</th>
                <th className="px-4 py-2 text-right">Outstanding</th>
                <th className="px-4 py-2 text-right">Chase</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.invoice_id} className="border-t" style={{ borderColor: "var(--ll-border)" }} data-testid={`ar-invoice-row-${inv.invoice_id}`}>
                  <td className="px-4 py-2 font-mono-fin">{inv.invoice_number || "—"}</td>
                  <td className="px-4 py-2 truncate max-w-[200px]" title={inv.bill_to_name}>{inv.bill_to_name}</td>
                  <td className="px-4 py-2 text-[var(--ll-ink-soft)]">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-4 py-2 text-[var(--ll-ink-soft)]">{inv.due_date ? fmtDate(inv.due_date) : "—"}</td>
                  <td className="px-4 py-2 text-right font-mono-fin" style={{ color: inv.days_overdue > 0 ? BUCKET_COLORS[inv.bucket] : "var(--ll-ink-muted)" }}>
                    {inv.days_overdue > 0 ? inv.days_overdue : "—"}
                  </td>
                  <td className="px-4 py-2">
                    <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest" style={{ background: BUCKET_COLORS[inv.bucket] + "22", color: BUCKET_COLORS[inv.bucket] }}>
                      {BUCKET_LABELS[inv.bucket]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono-fin">{fmtMoney(inv.outstanding)}</td>
                  <td className="px-4 py-2 text-right">
                    {inv.days_overdue >= 60 ? (
                      <button
                        type="button"
                        onClick={() => sendReminder(inv.invoice_id)}
                        disabled={remindingId === inv.invoice_id}
                        data-testid={`ar-remind-${inv.invoice_id}`}
                        className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2 py-1 border hover:bg-[#B45309] hover:text-white disabled:opacity-50"
                        style={{ borderColor: BUCKET_COLORS[inv.bucket], color: BUCKET_COLORS[inv.bucket] }}
                        title={`Send reminder email (${inv.days_overdue} days overdue)`}
                      >
                        {remindingId === inv.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <Bell size={11} />}
                        Remind
                      </button>
                    ) : (
                      <span className="text-xs text-[var(--ll-ink-muted)]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
